import { getCookie, setCookie, deleteCookie } from "../utils/cookies.ts";
import { getANPHost } from "../utils/host.ts";
import { withReferralParam } from "./referralStore.ts";

export interface ApiResponse<T> {
  status: number;
  data: T;
  message?: string;
}

export interface UserResponse {
  email: string;
  pk: string;
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  permissions: string[];
  roles?: string[];
  stripe_customer_id?: string;
  emails_subscribed?: boolean;
  /** The user's own code to share as `?ref=` in referral links. */
  referral_code?: string;
}

// Billing period as returned by the platform. Kept as a widened union so an
// unexpected server value still type-checks while the two known values keep
// autocomplete.
export type SubscriptionPeriod = "annually" | "monthly" | (string & {});

export interface ActiveSubscription {
  will_renew: boolean;
  /** ISO date string, e.g. "2027-01-31". `null` for dev users (no expiry). */
  expiration: string | null;
  period: SubscriptionPeriod;
}

/** Shape of DRF-style paginated list endpoints (only `results` is consumed). */
export interface PaginatedResponse<T> {
  results: T[];
}

/** Tokens as returned by POST /api/token/ (SimpleJWT). */
export interface LoginResponse {
  access: string;
  refresh: string;
}

/**
 * Tokens as returned by POST /api/rest-auth/google/ (dj-rest-auth social
 * login) — same idea as `LoginResponse`, but the field names carry a
 * `_token` suffix that SimpleJWT's own token endpoint doesn't use.
 */
export interface GoogleLoginResponse {
  access_token: string;
  refresh_token: string;
}

/** Tokens as returned by POST /api/rest-auth/facebook/ (dj-rest-auth social
 * login, same shape as `GoogleLoginResponse` - both go through the same
 * dj-rest-auth SocialLoginView machinery on the backend). */
export interface FacebookLoginResponse {
  access_token: string;
  refresh_token: string;
}

/** Tokens as returned by POST /api/rest-auth/apple/ (dj-rest-auth social
 * login). Same token pair as Google/Facebook - the difference is on the way
 * in: Apple hands the browser a one-time authorization *code*, which only
 * the backend can exchange (the exchange needs Apple's client secret, a
 * signed JWT that must never reach the browser). */
export interface AppleLoginResponse {
  access_token: string;
  refresh_token: string;
}

/**
 * POST /rest-auth/registration/ either logs the user in directly (tokens
 * present) or, when the backend requires e-mail verification, returns only
 * a human-readable `detail` message.
 */
export interface RegistrationResponse {
  access?: string;
  access_token?: string;
  refresh?: string;
  refresh_token?: string;
  detail?: string;
}

/**
 * Body of POST /rest-auth/password/change/. Field names are dj-rest-auth's
 * own (snake_case, two confirmation fields) — kept verbatim so the backend's
 * per-field error response maps straight back onto the form inputs.
 */
export interface ChangePasswordPayload {
  old_password: string;
  new_password1: string;
  new_password2: string;
}

/**
 * Body of POST /rest-auth/password/reset/. dj-rest-auth mails a reset link
 * to the address; our backend answers 401 when no account is registered
 * under it (stock dj-rest-auth would answer 200 either way, to avoid
 * leaking which addresses exist).
 */
export interface PasswordResetPayload {
  email: string;
}

/**
 * Body of POST /rest-auth/deactivate-account/. `reason` is the free-text
 * "other" answer when the user picked it, otherwise the selected reason's
 * label — same collapsing anp-fe's DeleteAccountForm.vue does.
 */
export interface DeactivateAccountPayload {
  password: string;
  reason: string;
}

/**
 * Thrown for any non-2xx response. Carries the parsed JSON body (dj-rest-auth
 * error responses are shaped like `{ non_field_errors: [...] }` or
 * per-field `{ email: [...], password1: [...] }`) so callers can show a
 * specific message instead of a generic "request failed".
 */
export class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

interface RequestOptions extends RequestInit {
  /** Skip attaching the Authorization header (and the 401-refresh-retry
   * dance) for endpoints that are unauthenticated by design — login,
   * registration, Google login. Mirrors anp-fe's axios interceptor, which
   * excludes these same endpoints from getting a bearer token. */
  skipAuth?: boolean;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string = getANPHost()) {
    this.baseURL = baseURL;
  }

  private getAccessToken(): string | null {
    return getCookie("accessToken");
  }

  private getRefreshToken(): string | null {
    return getCookie("refreshToken");
  }

  private async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      console.log("No refresh token available for refresh");
      return false;
    }

    try {
      console.log("Attempting to refresh access token...");
      const response = await fetch(`${this.baseURL}/api/token/refresh/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.access) {
          setCookie("accessToken", data.access);
          console.log("Access token refreshed successfully");
          return true;
        }
      } else if (response.status === 401) {
        // Refresh token is invalid, clear both tokens
        console.log("Refresh token is invalid, clearing tokens");
        deleteCookie("accessToken");
        deleteCookie("refreshToken");
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      // Clear tokens on network error as well
      deleteCookie("accessToken");
      deleteCookie("refreshToken");
    }

    return false;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestOptions = {},
    isRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    const { skipAuth, ...fetchOptions } = options;
    const accessToken = skipAuth ? null : this.getAccessToken();

    const config: RequestInit = {
      ...fetchOptions,
      headers: {
        "Content-Type": "application/json",
        ...fetchOptions.headers,
        // Only add Authorization header if we have a valid token
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    };

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, config);

      if (response.status === 401 && !isRetry && !skipAuth) {
        console.log(
          `401 Unauthorized received for ${endpoint}, attempting token refresh...`
        );
        // Token is invalid, try to refresh it
        const refreshSuccess = await this.refreshAccessToken();
        if (refreshSuccess) {
          // Retry the request with the new token
          console.log(`Retrying request to ${endpoint} with refreshed token`);
          return this.makeRequest<T>(endpoint, options, true);
        } else {
          // Refresh failed, throw authentication error
          console.log("Token refresh failed, authentication required");
          throw new Error("Authentication failed - refresh token invalid");
        }
      }

      if (response.status >= 400) {
        // Best-effort parse of the error body so callers (e.g. login/signup
        // forms) can surface the backend's actual message instead of a
        // generic one. The body may be empty or non-JSON, so this must not
        // throw itself.
        let data: unknown;
        try {
          data = await response.json();
        } catch {
          data = undefined;
        }
        const errorMessage = `Request failed with status ${response.status}`;
        console.error(`API Error: ${errorMessage} for endpoint ${endpoint}`);
        throw new ApiError(errorMessage, response.status, data);
      }

      const data = await response.json();

      return {
        status: response.status,
        data,
      };
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<ApiResponse<UserResponse>> {
    return this.makeRequest<UserResponse>("/rest-auth/user/");
  }

  /** Email/password login. Note the body field is `username`, not `email` —
   * the backend's token serializer expects it regardless of the account
   * actually being keyed by e-mail. */
  async login(email: string, password: string): Promise<ApiResponse<LoginResponse>> {
    return this.makeRequest<LoginResponse>("/api/token/", {
      method: "POST",
      body: JSON.stringify({ username: email, password }),
      skipAuth: true,
    });
  }

  /** Exchanges a Google Identity Services access token for our own JWT pair. */
  async loginWithGoogle(googleAccessToken: string): Promise<ApiResponse<GoogleLoginResponse>> {
    return this.makeRequest<GoogleLoginResponse>(withReferralParam("/api/rest-auth/google/"), {
      method: "POST",
      body: JSON.stringify({ access_token: googleAccessToken }),
      skipAuth: true,
    });
  }

  /** Exchanges a Facebook Login access token (response.authResponse.accessToken
   * from FB.login()) for our own JWT pair. */
  async loginWithFacebook(facebookAccessToken: string): Promise<ApiResponse<FacebookLoginResponse>> {
    return this.makeRequest<FacebookLoginResponse>(withReferralParam("/api/rest-auth/facebook/"), {
      method: "POST",
      body: JSON.stringify({ access_token: facebookAccessToken }),
      skipAuth: true,
    });
  }

  /** Exchanges an Apple authorization code (response.authorization.code from
   * AppleID.auth.signIn()) for our own JWT pair. Note the body key is `code`,
   * not `access_token` - the backend performs the code-for-token exchange
   * against Apple itself. */
  async loginWithApple(appleAuthorizationCode: string): Promise<ApiResponse<AppleLoginResponse>> {
    return this.makeRequest<AppleLoginResponse>(withReferralParam("/api/rest-auth/apple/"), {
      method: "POST",
      body: JSON.stringify({ code: appleAuthorizationCode }),
      skipAuth: true,
    });
  }

  async register(
    email: string,
    password1: string,
    password2: string
  ): Promise<ApiResponse<RegistrationResponse>> {
    return this.makeRequest<RegistrationResponse>(withReferralParam("/rest-auth/registration/"), {
      method: "POST",
      body: JSON.stringify({ email, password1, password2 }),
      skipAuth: true,
    });
  }

  /**
   * Requests a password-reset e-mail. Unauthenticated by design, hence
   * `skipAuth` — which also matters for error handling: without it a 401
   * would be swallowed by the refresh-and-retry branch above and surface as
   * a generic "Authentication failed" Error. With it, the backend's 401 for
   * an unknown e-mail reaches the caller as an `ApiError` it can recognise.
   */
  async requestPasswordReset(
    payload: PasswordResetPayload
  ): Promise<ApiResponse<{ detail?: string }>> {
    return this.makeRequest<{ detail?: string }>("/rest-auth/password/reset/", {
      method: "POST",
      body: JSON.stringify(payload),
      skipAuth: true,
    });
  }

  /** Changes the signed-in user's password. Rejects with `ApiError` whose
   * `data` carries dj-rest-auth's per-field lists (`old_password`,
   * `new_password1`, `new_password2`) for inline display. */
  async changePassword(
    payload: ChangePasswordPayload
  ): Promise<ApiResponse<{ detail?: string }>> {
    return this.makeRequest<{ detail?: string }>("/rest-auth/password/change/", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  /** Requests deactivation of the signed-in user's account. The current
   * password re-authenticates the request; the reason is collected for
   * churn analysis. */
  async deactivateAccount(
    payload: DeactivateAccountPayload
  ): Promise<ApiResponse<{ detail?: string }>> {
    return this.makeRequest<{ detail?: string }>(
      "/rest-auth/deactivate-account/",
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  }

  /**
   * Active subscription for a given permission (product). Same host/auth as
   * `getCurrentUser`. `page_size=1` because the caller only needs the current one.
   */
  async getActiveSubscription(
    permission: string
  ): Promise<ApiResponse<PaginatedResponse<ActiveSubscription>>> {
    const query = new URLSearchParams({ permission, page_size: "1" });
    return this.makeRequest<PaginatedResponse<ActiveSubscription>>(
      `/api/subscription/active/?${query.toString()}`
    );
  }

  async get<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: "GET", ...options });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });
  }

  async delete<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(endpoint, { method: "DELETE", ...options });
  }
}

export const apiClient = new ApiClient();
export default apiClient;
